import React, { useEffect, useMemo, useState } from 'react';
import DialogBox from './DialogBox';
import { FaCopy } from 'react-icons/fa';
import { copyToClipboard } from './includes/utils';
import {
	getAgentAccessInfo,
	checkAgentEndpoint,
	MCP_REPO_URL,
} from './includes/agentAccess';

/** Render one tool family (title, blurb, tool names). */
const ToolGroup = ({ group }) => (
	<div className="agent-tool-group">
		<div className="agent-tool-group-title">{group.title}</div>
		<div className="agent-tool-group-blurb">{group.blurb}</div>
		<div className="agent-tool-list">
			{group.tools.map((tool) => (
				<code key={tool} className="selectable">{tool}</code>
			))}
		</div>
	</div>
);

/**
 * "AI" titlebar dialog: shows how an agent drives this app over MCP.
 *
 * Context-specific first: when opened on a known program it leads with a
 * one-line summary of what an agent does with THAT app, a worked example
 * invocation, and just that app's tool families. The full catalogue and
 * generic setup ("the general stuff") sit behind a link so the focused
 * view stays focused. Unmapped programs open straight on the general view.
 */
const AgentAccessDialog = ({ progName, programTitle, onClose }) => {
	const info = useMemo(() => getAgentAccessInfo(progName), [progName]);
	// null = checking, true/false = result
	const [online, setOnline] = useState(null);
	// Specific windows start focused; the general catalogue is a click away.
	const [showAll, setShowAll] = useState(!info.specific);

	useEffect(() => {
		let cancelled = false;
		checkAgentEndpoint().then((up) => {
			if (!cancelled) setOnline(up);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	const statusText = online === null ? 'checking…' : online ? 'online' : 'unreachable';
	const statusColour = online === null ? '#808000' : online ? '#008000' : '#800000';
	const appLabel = programTitle || progName || 'this app';
	const example = info.example;

	return (
		<DialogBox
			title="Agent Access (MCP)"
			modal={true}
			icon=""
			dialogClass="agent-access-dialog-box"
			buttons={[{ label: 'Close', onClick: onClose }]}
			onClose={onClose}
			showMinMax={false}
		>
			<div className="agent-access-dialog">
				{info.specific ? (
					<>
						<div className="agent-app-head" data-testid="agent-app-head">
							Drive <strong>{appLabel}</strong> with an agent
						</div>
						{info.summary && <div className="agent-intro">{info.summary}</div>}
						{info.note && <div className="agent-note">{info.note}</div>}

						{example && (
							<div className="agent-example" data-testid="agent-example">
								<div className="agent-example-head">Worked example</div>
								<div className="agent-example-does">Ask your agent to {example.does}:</div>
								<code className="agent-example-call selectable">
									{example.tool}({JSON.stringify(example.args)})
								</code>
							</div>
						)}

						<div className="agent-tools">
							<div className="agent-tools-head">MCP tools behind {appLabel}:</div>
							{info.groups.map((group) => (
								<ToolGroup key={group.key} group={group} />
							))}
						</div>
					</>
				) : (
					<div className="agent-intro">
						Anything WINBIT32 can do, an agent should be able to do. Point your
						agent at the WINBIT32 MCP server and it gets the tool families below
						— keys stay with you: signing happens via the cosign QR or direct
						entry, never on the server.
					</div>
				)}

				{/* The general stuff: full catalogue + setup. A link when focused. */}
				{info.specific && (
					<button
						type="button"
						className="agent-general-toggle"
						data-testid="agent-general-toggle"
						aria-expanded={showAll}
						onClick={() => setShowAll((v) => !v)}
					>
						{showAll
							? '▾ Hide general agent access'
							: `▸ All ${info.toolCount} MCP tools & setup`}
					</button>
				)}

				{showAll && (
					<div className="agent-general" data-testid="agent-general">
						<div className="agent-endpoint">
							<span>Endpoint:</span>
							<span className="selectable">{info.endpoint}</span>
							<span className="agent-status" style={{ color: statusColour }} data-testid="agent-status">
								● {statusText}
							</span>
						</div>

						<div className="agent-config">
							<div className="agent-config-head">
								<span>Agent config (Cursor / Claude style):</span>
								<button
									type="button"
									title="Copy config"
									onClick={() => copyToClipboard(info.configSnippet)}
								>
									<FaCopy /> Copy
								</button>
							</div>
							<textarea value={info.configSnippet} readOnly rows={7} spellCheck={false} />
						</div>

						<div className="agent-tools">
							<div className="agent-tools-head">Full tool catalogue:</div>
							{info.allGroups.map((group) => (
								<ToolGroup key={group.key} group={group} />
							))}
						</div>

						<div className="agent-footer">
							Server source &amp; full docs:{' '}
							<a href={MCP_REPO_URL} target="_blank" rel="noreferrer">
								{MCP_REPO_URL.replace('https://', '')}
							</a>
						</div>
					</div>
				)}
			</div>
		</DialogBox>
	);
};

export default AgentAccessDialog;
